import * as ko from "knockout";
import {TBaseOrObservableOrFunctionArrayType} from "../../index";
import {getAnyValue, getNextId} from "../../util/misc";
import {IDropdownButton} from "../button/index";
import ellipsis_dropdownHtml from './ellipsis-dropdown.html?raw';

export const template = ellipsis_dropdownHtml

export interface IParams {
    buttons: TBaseOrObservableOrFunctionArrayType<IDropdownButton>
    icon?: string
    leadingIcon?: string
    label?: string
    btnClass?: string
    title?: string
    validator?: () => boolean
}

export class viewModel {

    private buttons: TBaseOrObservableOrFunctionArrayType<IDropdownButton>
    private icon: string
    private leadingIcon: string | null
    private label: string | null
    private btnClass: string
    private validator: () => boolean
    private readonly isOpen = ko.observable(false)
    /** Unique CSS anchor name so multiple menus on one page don't fight. */
    private readonly id = `ellipsis-dropdown-${getNextId()}`
    private readonly anchorName = `--${this.id}`
    private readonly anchorStyle = {'anchor-name': this.anchorName}
    private readonly menuStyle = {'position-anchor': this.anchorName}
    private readonly menuId = `${this.id}-menu`

    constructor(private params: IParams) {
        this.buttons = params.buttons
        this.icon = params.icon ?? 'fa-ellipsis-v'
        this.leadingIcon = params.leadingIcon ?? null
        this.label = params.label ?? null
        this.btnClass = params.btnClass ?? 'btn btn-func'
        this.validator = params.validator ?? (() => true)
        document.addEventListener('pointerdown', this.onDocumentPointerDown, true)
    }

    private hasButtons(): boolean {
        const buttons = this.getButtons();
        return buttons.length > 0 && buttons.some(b => b.validator?.() ?? true)
    }

    private hasContents = ko.pureComputed(() => {
        return this.validator() && this.getButtons().reduce((acc: boolean, curr: IDropdownButton) => acc || (curr.validator?.() ?? true), false)
    })

    private getButtons(): IDropdownButton[] {
        return getAnyValue(this.buttons) ?? []
    }

    private getMenuButtons(): IDropdownButton[] {
        return this.getButtons().map(button => ({
            ...button,
            attributes: {
                ...button.attributes,
                role: 'menuitem',
                tabindex: -1,
            },
        }))
    }

    private onToggleClick = (): void => {
        this.isOpen(!this.isOpen())
    }

    private onToggleKeyDown = (_: unknown, event: KeyboardEvent): boolean => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            this.openAndFocus(0)
            return false
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            this.openAndFocus(-1)
            return false
        }
        if (event.key === 'Escape') {
            this.closeAndFocusToggle()
            return false
        }
        return true
    }

    private onMenuKeyDown = (_: unknown, event: KeyboardEvent): boolean => {
        const items = this.getMenuItems()
        const currentIndex = items.indexOf(event.target as HTMLElement)
        let nextIndex: number | null = null

        switch (event.key) {
            case 'ArrowDown':
                nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
                break
            case 'ArrowUp':
                nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
                break
            case 'Home':
                nextIndex = 0
                break
            case 'End':
                nextIndex = items.length - 1
                break
            case 'Escape':
                event.preventDefault()
                this.closeAndFocusToggle()
                return false
            case 'Tab':
                this.isOpen(false)
                return true
            default:
                return true
        }

        event.preventDefault()
        items[nextIndex]?.focus()
        return false
    }

    private onFocusOut = (_: unknown, event: FocusEvent): boolean => {
        const container = document.getElementById(this.id)
        if (!container?.contains(event.relatedTarget as Node | null)) {
            this.isOpen(false)
        }
        return true
    }

    private onDocumentPointerDown = (event: PointerEvent): void => {
        if (!document.getElementById(this.id)?.contains(event.target as Node)) {
            this.isOpen(false)
        }
    }

    private openAndFocus(index: number): void {
        this.isOpen(true)
        requestAnimationFrame(() => {
            const items = this.getMenuItems()
            items[index < 0 ? items.length - 1 : index]?.focus()
        })
    }

    private closeAndFocusToggle(): void {
        this.isOpen(false)
        document.querySelector<HTMLElement>(`#${this.id} > button`)?.focus()
    }

    private getMenuItems(): HTMLElement[] {
        return Array.from(
            document.querySelectorAll<HTMLElement>(`#${this.menuId} > [role="menuitem"]:not(:disabled)`),
        )
    }

    dispose(): void {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown, true)
        this.hasContents.dispose()
    }
}
